/**
 * Local SQLite adapters for hotspots.
 * Mirrors the Convex api.hotspots.* interface.
 */
import { localDb, newId, now } from "@/lib/localDb";
import { invalidate } from "./queryCache";
import { useLocalQuery } from "./useLocalQuery";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface LocalHotspot {
  _id: string;
  project_id: string;
  panorama_id: string | null;
  name?: string;
  type?: string;
  content?: Record<string, unknown>;
  position?: { x: number; y: number; z: number };
  rotation?: { x: number; y: number; z: number };
  scale?: { x: number; y: number; z: number };
  visible?: boolean;
  order?: number;
  created_at?: string;
  updated_at?: string;
}

const SELECT_HOTSPOT = `
  SELECT id as _id, id, panorama_id, project_id, type as type, label as name,
    content, style,
    position_x, position_y, position_z,
    rotation_x, rotation_y, rotation_z,
    scale_x, scale_y, scale_z,
    visible, \`order\`,
    created_at, updated_at
  FROM hotspots`;

// ─── Queries ──────────────────────────────────────────────────────────────────

export function useHotspotsByProject(
  projectId: string | undefined,
  craftspaceId: string | undefined,
): LocalHotspot[] | undefined {
  return useLocalQuery<LocalHotspot>(
    `${SELECT_HOTSPOT}
     WHERE project_id = ?
     ORDER BY \`order\` ASC, created_at ASC`,
    [projectId ?? ""],
    ["hotspots"],
    ["content", "style"],
    craftspaceId,
  );
}

export function useHotspotsByPanorama(
  panoramaId: string | undefined,
  craftspaceId: string | undefined,
): LocalHotspot[] | undefined {
  return useLocalQuery<LocalHotspot>(
    `${SELECT_HOTSPOT}
     WHERE panorama_id = ?
     ORDER BY \`order\` ASC, created_at ASC`,
    [panoramaId ?? ""],
    ["hotspots"],
    ["content", "style"],
    craftspaceId,
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function posToXYZ(pos: { x: number; y: number; z: number } | undefined): [number, number, number] {
  return [pos?.x ?? 0, pos?.y ?? 0, pos?.z ?? 0];
}

function rotationToXYZ(
  rotation: { x: number; y: number; z: number } | undefined,
): [number, number, number] {
  return [rotation?.x ?? 0, rotation?.y ?? 0, rotation?.z ?? 0];
}

function scaleToXYZ(
  scale: { x: number; y: number; z: number } | undefined,
): [number, number, number] {
  return [scale?.x ?? 1, scale?.y ?? 1, scale?.z ?? 1];
}

// ─── Mutations ────────────────────────────────────────────────────────────────

export async function createHotspot(params: {
  project_id: string;
  craftspace_id: string;
  panorama_id: string;
  name?: string;
  type?: string;
  content?: Record<string, unknown>;
  position?: { x: number; y: number; z: number };
  rotation?: { x: number; y: number; z: number };
  scale?: { x: number; y: number; z: number };
  visible?: boolean;
  order?: number;
}): Promise<string> {
  const id = newId();
  const ts = now();
  const [px, py, pz] = posToXYZ(params.position);
  const [rx, ry, rz] = rotationToXYZ(params.rotation);
  const [sx, sy, sz] = scaleToXYZ(params.scale);

  await localDb(params.craftspace_id).run(
    `INSERT INTO hotspots (id, panorama_id, project_id, type, label,
       position_x, position_y, position_z,
       rotation_x, rotation_y, rotation_z,
       scale_x, scale_y, scale_z,
       visible, content, style, \`order\`, created_at, updated_at)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    [
      id,
      params.panorama_id,
      params.project_id,
      params.type ?? "navigation",
      params.name ?? null,
      px,
      py,
      pz,
      rx,
      ry,
      rz,
      sx,
      sy,
      sz,
      params.visible ?? true ? 1 : 0,
      params.content ? JSON.stringify(params.content) : null,
      null, // style
      params.order ?? 0,
      ts,
      ts,
    ],
  );
  invalidate("hotspots");
  return id;
}

export async function updateHotspot(params: {
  id: string;
  craftspace_id: string;
  panorama_id?: string | null;
  name?: string;
  type?: string;
  content?: Record<string, unknown>;
  position?: { x: number; y: number; z: number };
  rotation?: { x: number; y: number; z: number };
  scale?: { x: number; y: number; z: number };
  visible?: boolean;
  order?: number;
}): Promise<void> {
  const { id, craftspace_id, ...fields } = params;
  const setClauses: string[] = ["updated_at = ?"];
  const values: unknown[] = [now()];

  if (fields.panorama_id !== undefined) { setClauses.push("panorama_id = ?"); values.push(fields.panorama_id); }
  if (fields.name !== undefined) { setClauses.push("label = ?"); values.push(fields.name); }
  if (fields.type !== undefined) { setClauses.push("type = ?"); values.push(fields.type); }
  if (fields.content !== undefined) { setClauses.push("content = ?"); values.push(JSON.stringify(fields.content)); }
  if (fields.order !== undefined) { setClauses.push("`order` = ?"); values.push(fields.order); }
  if (fields.position !== undefined) {
    setClauses.push("position_x = ?, position_y = ?, position_z = ?");
    const [px, py, pz] = posToXYZ(fields.position);
    values.push(px, py, pz);
  }
  if (fields.rotation !== undefined) {
    setClauses.push("rotation_x = ?, rotation_y = ?, rotation_z = ?");
    const [rx, ry, rz] = rotationToXYZ(fields.rotation);
    values.push(rx, ry, rz);
  }
  if (fields.scale !== undefined) {
    setClauses.push("scale_x = ?, scale_y = ?, scale_z = ?");
    const [sx, sy, sz] = scaleToXYZ(fields.scale);
    values.push(sx, sy, sz);
  }
  if (fields.visible !== undefined) {
    setClauses.push("visible = ?");
    values.push(fields.visible ? 1 : 0);
  }

  values.push(id);
  await localDb(craftspace_id).run(`UPDATE hotspots SET ${setClauses.join(", ")} WHERE id = ?`, values);
  invalidate("hotspots");
}

export async function removeHotspot(id: string, craftspaceId: string): Promise<void> {
  await localDb(craftspaceId).run("DELETE FROM hotspots WHERE id = ?", [id]);
  invalidate("hotspots");
}
